
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Building, Home, Briefcase } from 'lucide-react';
import DataTable, { Column } from '@/components/ui/DataTable';
import StatusBadge from '@/components/ui/StatusBadge';
import { Subscription } from '@/lib/data';
import { cn } from '@/lib/utils';

interface SubscriptionTableProps {
  subscriptions: Subscription[];
  isLoading?: boolean;
  className?: string;
}

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  subscriptions,
  isLoading = false,
  className,
}) => {
  const navigate = useNavigate();

  const handleRowClick = (subscription: Subscription) => {
    navigate(`/subscriptions/${subscription.subscription_id}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      // Handle both date strings and full ISO date strings
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '-';
    }
  };

  const getHousingTypeIcon = (type: 'house' | 'apartment' | 'office' | undefined) => {
    switch (type) {
      case 'house':
        return <Home className="h-4 w-4 text-green-600" />;
      case 'apartment':
        return <Building className="h-4 w-4 text-blue-600" />;
      case 'office':
        return <Briefcase className="h-4 w-4 text-purple-600" />;
      default:
        return <Home className="h-4 w-4 text-green-600" />;
    }
  };

  const columns: Column<Subscription>[] = [
    {
      key: 'subscription_number',
      header: 'Subscription',
      cell: (subscription) => (
        <div>
          <div className="font-medium">{subscription.subscription_number}</div>
        </div>
      ),
      sortable: true,
      className: 'w-32',
    },
    {
      key: 'customer.customer_name',
      header: 'Customer',
      cell: (subscription) => {
        // Check if customer data exists and has a name
        const customerName = subscription.customer?.customer_name;
        const customerNumber = subscription.customer?.customer_number;
        
        return (
          <div>
            <div className="font-medium">
              {customerName || 'Unknown Customer'}
              {!customerName && subscription.customer_id && 
                <span className="text-xs ml-1 text-muted-foreground">(ID: {subscription.customer_id.substring(0, 8)}...)</span>
              }
            </div>
            <div className="text-xs text-muted-foreground">{customerNumber || '-'}</div>
          </div>
        );
      },
      sortable: true,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (subscription) => <StatusBadge status={subscription.status} />,
      sortable: true,
      className: 'w-32',
    },
    {
      key: 'housing_type',
      header: 'Type',
      cell: (subscription) => (
        <div className="flex items-center">
          {getHousingTypeIcon(subscription.housing_type)}
          <span className="ml-2 capitalize">{subscription.housing_type || 'house'}</span>
        </div>
      ),
      sortable: true,
      className: 'w-32',
    },
    {
      key: 'installation_city',
      header: 'Location',
      cell: (subscription) => (
        <div>
          <div>{subscription.installation_city || '-'}</div>
          <div className="text-xs text-muted-foreground">{subscription.installation_zip_code || '-'}</div>
        </div>
      ),
      sortable: true,
    },
    {
      key: 'next_bill_date',
      header: 'Next Bill',
      cell: (subscription) => formatDate(subscription.next_bill_date),
      sortable: true,
      className: 'w-32',
    },
    // Services column removed as requested
  ];

  return (
    <div className={cn("glass-card rounded-lg overflow-hidden", className)}>
      <DataTable
        data={subscriptions}
        columns={columns}
        keyField="subscription_id"
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SubscriptionTable;
